import { Editor } from '@tiptap/react';
import {
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ConfigContext, UserContext } from '../../../../context';
import {
  ApiClientType,
  Event,
  OrganisationTag,
  Story,
  StoryCard,
  StoryCardArticleParsedUrl,
  StoryCardAudioParsedUrl,
  StoryCardFieldsPostInStoryInput,
  StoryCardImageParsedUrl,
  StoryCardPdfParsedUrl,
  StoryCardPollContentOption,
  StoryCardPostContentBlockFields,
  StoryCardQuoteParsedUrl,
  StoryCardStatus,
  StoryCardTeaserImageStyle,
  StoryCardTemplateStoryCardInput,
  StoryCardType,
  StoryCardVideoParsedUrl,
  User,
  storyCardApi,
} from '../../../../services';
import { addCard, editCard, getCard } from '../../../../slices';
import { LOCALES, POST_IN_STORAGE_KEY } from '../../../constants';
import {
  CardProps,
  useAppDispatch,
  useFilter,
  useRefreshFeeds,
  useRefreshStoryCardsUserStats,
  useTags,
} from '../../../hooks';
import { GalleryItem } from '../../../models';
import {
  generateRandomString,
  getChannelDomain,
  getCssVar,
  getWebAppUrl,
  isAdminLayout,
  isCardMediaCompleted,
  optArrItem,
  toggleStoryCardDisabled,
} from '../../../utils';
import { ConfirmationModal } from '../../ConfirmationModal';
import { FilterValue } from '../../Filter';
import { IconLabel } from '../../IconLabel';
import { Modal } from '../../Modal';
import { TabItem, Tabs } from '../../Tabs';
import { UserMentionModal } from '../../UserMentionModal';
import { EventPost } from '../EventPost';
import { FullPost } from '../FullPost';
import { IgdbPost } from '../IgdbPost';
import { LinkPost } from '../LinkPost';
import { PollPost } from '../PollPost';
import { ShortPost } from '../ShortPost';
import { ThreadPost } from '../ThreadPost';
import { CreateCardButtons } from './CreateCardButtons';
import { CreateCardHeader } from './CreateCardHeader';
import { CreateCardSettings, CreateCardSettingsTabType } from './CreateCardSettings';
import {
  getBackgroundColorGroupItems,
  getCardGroupItems,
  getFieldsByStoryCardType,
  getLocaleGroupItems,
  getPostInCardType,
  getPostTypeValueDefault,
  usePostInStoryIdDefault,
} from './helpers';

import classNames from 'classnames';
import classes from './CreateCardModal.module.scss';

export enum PostType {
  SHORT = 'SHORT',
  FULL = 'FULL',
  LINK = 'LINK',
  THREAD = 'THREAD',
  EVENT = 'EVENT',
  POLL = 'POLL',
  IGDB = 'IGDB',
}

enum TabType {
  SHORT_TAB,
  FULL_TAB,
  LINK_TAB,
  THREAD_TAB,
  EVENT_TAB,
  POLL_TAB,
  IGDB_TAB,
}

export interface CardSettings {
  card: FilterValue[];
}

export interface ShortPostFields {
  sourceName: string;
  headline: string;
  subHeadline: string;
  text: string;
  gallery: GalleryItem[];
}

export interface FullPostFields {
  sourceName: string;
  title: string;
  headline: string;
  abstract: string;
  contentAuthor: string;
  gallery: GalleryItem[];
  styles: { teaserImageStyle: StoryCardTeaserImageStyle };
  contentBlocks: StoryCardPostContentBlockFields[];
}

export interface ThreadPostFields {
  title: string;
}

export interface LinkPostQuoteFields extends StoryCardQuoteParsedUrl {
  headline: string;
}

export interface LinkPostArticleFields extends StoryCardArticleParsedUrl {
  headline: string;
  styles: { teaserImageStyle: StoryCardTeaserImageStyle };
  urlFormatted?: string | null;
}

export interface LinkPostFields {
  url: string;
  type?: StoryCardType;
  article?: LinkPostArticleFields;
  quote?: LinkPostQuoteFields;
  image?: StoryCardImageParsedUrl;
  audio?: StoryCardAudioParsedUrl;
  video?: StoryCardVideoParsedUrl;
  pdf?: StoryCardPdfParsedUrl;
}

export interface PollPostFields {
  headline: string;
  text: string;
  options: StoryCardPollContentOption[];
  allowAnonymous: boolean;
  multipleVotes: boolean;
  showVoteResults: boolean;
  gallery: GalleryItem[];
  totalVotes: number; //* artificial field, used to disable fields
  showTotalVotes: boolean;
  votingTime: string | null;
}

export interface EventPostFields {
  event: Event | null;
  headline: string;
}

interface GalleryConfig {
  galleryItems: GalleryItem[];
  setGalleryItems: (gallery: GalleryItem[]) => void;
  teaserStyle?: StoryCardTeaserImageStyle;
  setTeaserStyle?: (teaserImageStyle: StoryCardTeaserImageStyle) => void;
}

interface CreateCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  postInStories: Story[];
  card?: Partial<StoryCard>;
  shortPostFilledFields?: ShortPostFields;
  fullPostFilledFields?: FullPostFields;
  linkPostFilledFields?: LinkPostFields;
  threadPostFilledFields?: ThreadPostFields;
  pollPostFilledFields?: PollPostFields;
  eventPostFilledFields?: EventPostFields;
  files?: FileList;
  fileUrl?: string;
  applePodcastUrl?: string;
  isTemplatePost?: boolean;
  onTemplateSave?: (storyCard: StoryCardTemplateStoryCardInput) => void;
  isTemplateSaving?: boolean;
}

export interface ConvertToLinkPostArgs {
  url: string;
  headline: string;
}

const FETCH_CARD_ATTEMPTS_LIMIT = 2;

const FETCH_CARD_MS_TIMEOUT = 5000;

export const CreateCardModal: FunctionComponent<CreateCardModalProps> = memo(
  ({
    isOpen,
    onClose,
    postInStories,
    card,
    shortPostFilledFields,
    fullPostFilledFields,
    linkPostFilledFields,
    threadPostFilledFields,
    pollPostFilledFields,
    eventPostFilledFields,
    files,
    fileUrl,
    applePodcastUrl,
    isTemplatePost,
    onTemplateSave,
    isTemplateSaving,
  }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'addContent' });

    const dispatch = useAppDispatch();

    const {
      excludeLongPost,
      excludeThreadPost,
      excludeReaderPollPost,
      excludeAdminPollPost,
      excludeReaderEventPost,
      excludeAdminEventPost,
      enableIgdb,
      igdbLocaliseKey,
    } = useContext(ConfigContext).config.elements.addContent;

    const [storyCardPostInStory] = storyCardApi.endpoints.storyCardPostInStory.useMutation();

    const [storyCardUpdate] = storyCardApi.endpoints.storyCardUpdate.useMutation();

    const { refreshFeeds } = useRefreshFeeds({ scrollTop: false });

    const { refreshStoryCardsUserStats } = useRefreshStoryCardsUserStats();

    const { getGroupValue, getGroupItemValue } = useFilter();

    const { hasContentTags } = useTags();

    const { channelName } = useContext(UserContext).userInfo.body;

    const fetchCardAttemptsCount = useRef<number>(0);

    const shortPostEditorRef = useRef<Editor | null>(null);

    const {
      id: cardId = 0,
      storyId: cardStoryId,
      status: cardStatus,
      options: cardOptions,
      category: cardCategory,
      mentionedUsers: cardMentionedUsers = [],
      postingTime: cardPostingTime,
      externalTags: cardExternalTags,
      externalStatusTags: cardExternalStatusTags,
      internalTags: cardInternalTags,
      content: cardContent,
      localeId,
      apiClient,
      assignedUserId,
    } = { ...card };

    const postInStorageKey = useMemo(() => `${POST_IN_STORAGE_KEY}_${channelName}`, [channelName]);

    const [postInStoryId, setPostInStoryId] = useState<number>(
      usePostInStoryIdDefault({ postInStories, postInStorageKey, cardStoryId })
    );

    const [loading, setLoading] = useState(false);

    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

    const {
      id: categoryId,
      name: categoryName,
      story: categoryStory,
      settings: categorySettings,
    } = { ...cardCategory };

    const { id: categoryStoryId } = { ...categoryStory };

    const editMode = useMemo(() => Boolean(cardId), [cardId]);

    const [userMentionModalOpen, setUserMentionModalOpen] = useState<boolean>(
      Boolean(categorySettings?.userSelection && !editMode)
    );

    const isCategoryCard = useMemo(() => Boolean(categoryId), [categoryId]);

    const isTemplateEditor = Boolean(onTemplateSave);

    const userMentionModalClose = useCallback(() => {
      setUserMentionModalOpen(false);

      if (isCategoryCard && shortPostEditorRef.current) {
        shortPostEditorRef.current.commands.focus('end');
      }
    }, [isCategoryCard]);

    const [settingsOpen, setSettingsOpen] = useState<boolean>(false);

    const settingsButton = useRef<HTMLDivElement>(null);

    const scrollToSettings = useCallback(() => {
      setTimeout(
        () => settingsButton?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        100
      );
    }, []);

    const setSettingsOpenWithScroll = useCallback(
      (isOpen: boolean) => {
        setSettingsOpen(isOpen);
        scrollToSettings();
      },
      [scrollToSettings]
    );

    const [postType, setPostType] = useState<PostType>(
      getPostTypeValueDefault({
        fullPostFilledFields,
        linkPostFilledFields,
        threadPostFilledFields,
        pollPostFilledFields,
        eventPostFilledFields,
      })
    );

    const isShortPost = useMemo(() => postType === PostType.SHORT, [postType]);

    const isFullPost = useMemo(() => postType === PostType.FULL, [postType]);

    const isLinkPost = useMemo(() => postType === PostType.LINK, [postType]);

    const isIgdbPost = useMemo(() => postType === PostType.IGDB, [postType]);

    const isIntegrationCard = useMemo(
      () => Boolean(!assignedUserId && apiClient?.type === ApiClientType.API_CLIENTS_EXTERNAL),
      [apiClient?.type, assignedUserId]
    );

    const [postingTime, setPostingTime] = useState<Date | null>(
      cardPostingTime ? new Date(cardPostingTime) : null
    );

    const postInStory = useMemo(
      () => postInStories.find(({ id }) => id === postInStoryId),
      [postInStories, postInStoryId]
    );

    const postInStoryTeaserStyle = useMemo(
      () =>
        postInStory?.settings.cardStyles.article.teaserStyle ?? StoryCardTeaserImageStyle.STANDARD,
      [postInStory?.settings.cardStyles.article.teaserStyle]
    );

    const postInStoryAllowMentions = useMemo(
      () => Boolean(!isTemplateEditor && postInStory?.settings.allowCardMentions),
      [isTemplateEditor, postInStory?.settings.allowCardMentions]
    );

    const [externalTags, setExternalTags] = useState<OrganisationTag[]>(
      (!editMode ? postInStory?.externalTags : cardExternalTags) ?? []
    );

    const [externalStatusTags, setExternalStatusTags] = useState<OrganisationTag[]>(
      (!editMode ? postInStory?.externalStatusTags : cardExternalStatusTags) ?? []
    );

    const [internalTags, setInternalTags] = useState<OrganisationTag[]>(
      (!editMode ? postInStory?.internalTags : cardInternalTags) ?? []
    );

    const [mentionedUsers, setMentionedUsers] = useState<User[]>(cardMentionedUsers);

    useEffect(() => {
      if (editMode) {
        return;
      }

      setExternalTags(postInStory?.externalTags ?? []);
      setExternalStatusTags(postInStory?.externalStatusTags ?? []);
      setInternalTags(postInStory?.internalTags ?? []);
    }, [editMode, postInStory]);

    useEffect(() => {
      if (editMode) {
        return;
      }

      if (!postInStoryAllowMentions) {
        setMentionedUsers([]);
      }
    }, [editMode, postInStoryAllowMentions]);

    const [shortPostFields, setShortPostFields] = useState<ShortPostFields>(
      shortPostFilledFields ?? {
        sourceName: '',
        headline: '',
        subHeadline: '',
        text: '',
        gallery: [],
      }
    );

    const setShortPostGalleryItems = useCallback(
      (gallery: GalleryItem[]) => setShortPostFields({ ...shortPostFields, gallery }),
      [setShortPostFields, shortPostFields]
    );

    const [fullPostFields, setFullPostFields] = useState<FullPostFields>(
      fullPostFilledFields ?? {
        sourceName: '',
        title: '',
        headline: '',
        abstract: '',
        contentAuthor: '',
        gallery: [],
        contentBlocks: [],
        styles: { teaserImageStyle: postInStoryTeaserStyle },
      }
    );

    const setFullPostGalleryItems = useCallback(
      (gallery: GalleryItem[]) => setFullPostFields({ ...fullPostFields, gallery }),
      [fullPostFields]
    );

    const setFullPostTeaserStyle = useCallback(
      (teaserImageStyle: StoryCardTeaserImageStyle) =>
        setFullPostFields({ ...fullPostFields, styles: { teaserImageStyle } }),
      [fullPostFields, setFullPostFields]
    );

    const [linkPostFields, setLinkPostFields] = useState<LinkPostFields>(
      linkPostFilledFields ?? { url: '' }
    );

    const [threadPostFields, setThreadPostFields] = useState<ThreadPostFields>(
      threadPostFilledFields ?? { title: '' }
    );

    const [pollPostFields, setPollPostFields] = useState<PollPostFields>(
      pollPostFilledFields ?? {
        headline: '',
        text: '',
        options: [
          { id: generateRandomString(4), text: '' },
          { id: generateRandomString(4), text: '' },
        ],
        allowAnonymous: true,
        multipleVotes: false,
        showVoteResults: false,
        gallery: [],
        totalVotes: 0,
        showTotalVotes: true,
        votingTime: null,
      }
    );

    const [eventPostFields, setEventPostFields] = useState<EventPostFields>(
      eventPostFilledFields ?? { event: null, headline: '' }
    );

    const setPollPostGalleryItems = useCallback(
      (gallery: GalleryItem[]) => setPollPostFields({ ...pollPostFields, gallery }),
      [pollPostFields]
    );

    const [activeSettingsTabIndex, setActiveSettingsTabIndex] = useState(
      isAdminLayout() ? CreateCardSettingsTabType.CARD : CreateCardSettingsTabType.TAGS
    );

    const { text: shortPostText, gallery: shortPostGallery } = shortPostFields;

    const {
      title: fullPostTitle,
      gallery: fullPostGallery,
      styles: { teaserImageStyle: fullPostTeaserStyle },
      contentBlocks: fullPostContentBlocks,
    } = fullPostFields;

    const {
      url: linkPostFieldsUrl,
      type: linkPostType,
      article: linkPostArticle,
      quote: linkPostQuote,
    } = linkPostFields;

    const { styles: linkPostArticleStyles, gallery: linkPostArticleGallery } = {
      ...linkPostArticle,
    };

    const { gallery: linkPostQuoteGallery } = { ...linkPostQuote };

    const { title: threadPostTitle } = threadPostFields;

    const {
      headline: pollPostHeadline,
      options: pollPostOptions,
      gallery: pollPostGallery,
    } = pollPostFields;

    const eventId = eventPostFields.event?.id ?? null;

    const hasUnsavedContent = useMemo(() => {
      if (editMode) {
        return true;
      }
      switch (postType) {
        case PostType.SHORT:
          return (
            Boolean(shortPostFields.text) ||
            Boolean(shortPostFields.headline) ||
            Boolean(shortPostFields.subHeadline) ||
            Boolean(shortPostFields.gallery.length)
          );
        case PostType.FULL:
          return (
            Boolean(fullPostFields.title) ||
            Boolean(fullPostFields.headline) ||
            Boolean(fullPostFields.abstract) ||
            Boolean(fullPostFields.contentAuthor) ||
            Boolean(fullPostFields.contentBlocks.length) ||
            Boolean(fullPostFields.gallery.length)
          );
        case PostType.LINK:
          return Boolean(linkPostFields.url);
        case PostType.THREAD:
          return Boolean(threadPostFields.title);
        case PostType.POLL:
          return (
            Boolean(pollPostFields.headline) ||
            Boolean(pollPostFields.text) ||
            Boolean(pollPostFields.gallery.length) ||
            pollPostFields.options.some(({ text }) => Boolean(text))
          );
        case PostType.EVENT:
          return Boolean(eventPostFields.event) || Boolean(eventPostFields.headline);
        case PostType.IGDB:
          return Boolean(linkPostFields.article);
        default:
          return false;
      }
    }, [
      editMode,
      postType,
      shortPostFields,
      fullPostFields,
      linkPostFields,
      threadPostFields,
      pollPostFields,
      eventPostFields,
    ]);

    const onCloseHandler = useCallback(() => {
      hasUnsavedContent ? setShowCloseConfirmation(true) : onClose();
    }, [hasUnsavedContent, onClose]);

    useEffect(() => {
      if (!editMode) {
        const styles = { teaserImageStyle: postInStoryTeaserStyle };

        setFullPostFields({ ...fullPostFields, styles });

        if (linkPostFields.article) {
          setLinkPostFields({ ...linkPostFields, article: { ...linkPostFields.article, styles } });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postInStoryTeaserStyle]);

    const setLinkPostArticleGalleryItems = useCallback(
      (gallery: GalleryItem[]) => {
        if (!linkPostFields.article) {
          return;
        }

        setLinkPostFields({ ...linkPostFields, article: { ...linkPostFields.article, gallery } });
      },
      [linkPostFields]
    );

    const setLinkPostArticleTeaserStyle = useCallback(
      (teaserImageStyle: StoryCardTeaserImageStyle) => {
        if (!linkPostFields.article) {
          return;
        }

        setLinkPostFields({
          ...linkPostFields,
          article: { ...linkPostFields.article, styles: { teaserImageStyle } },
        });
      },
      [linkPostFields]
    );

    const setLinkPostQuoteGalleryItems = useCallback(
      (gallery: GalleryItem[]) => {
        if (!linkPostFields.quote) {
          return;
        }

        setLinkPostFields({ ...linkPostFields, quote: { ...linkPostFields.quote, gallery } });
      },
      [linkPostFields]
    );

    const [fullPostTextEditorOpen, setFullPostTextEditorOpen] = useState(
      Boolean(!editMode && !fullPostContentBlocks.length)
    );

    const [fullPostTextEditorUsePrefill, setFullPostTextEditorUsePrefill] = useState(
      Boolean(!editMode)
    );

    const [linkPostPrevUrl, setLinkPostPrevUrl] = useState<string>(
      editMode || isTemplatePost ? linkPostFieldsUrl : ''
    );

    const convertToLinkPost = useCallback(
      ({ url, headline }: ConvertToLinkPostArgs) => {
        if (editMode || url === linkPostFieldsUrl) {
          return;
        }

        setLinkPostFields({
          url,
          article: { headline } as LinkPostArticleFields,
          quote: { headline } as LinkPostQuoteFields,
        });

        setPostType(PostType.LINK);
      },
      [editMode, linkPostFieldsUrl]
    );

    const postInCardType = useMemo(
      () => getPostInCardType({ postType, shortPostGallery, linkPostArticle, linkPostQuote }),
      [linkPostArticle, linkPostQuote, postType, shortPostGallery]
    );

    const isArticle = useMemo(() => postInCardType === StoryCardType.ARTICLE, [postInCardType]);

    const { galleryItems, teaserStyle, setTeaserStyle } = useMemo<GalleryConfig>(() => {
      const articleGallery = {
        galleryItems: linkPostArticleGallery ?? [],
        setGalleryItems: setLinkPostArticleGalleryItems,
        teaserStyle: linkPostArticleStyles?.teaserImageStyle,
        setTeaserStyle: setLinkPostArticleTeaserStyle,
      };

      switch (postType) {
        case PostType.SHORT:
          return {
            galleryItems: shortPostGallery,
            setGalleryItems: setShortPostGalleryItems,
          };
        case PostType.FULL:
          return {
            galleryItems: fullPostGallery,
            setGalleryItems: setFullPostGalleryItems,
            teaserStyle: fullPostTeaserStyle,
            setTeaserStyle: setFullPostTeaserStyle,
          };
        case PostType.LINK:
          if (isArticle) {
            return articleGallery;
          }
          return {
            galleryItems: linkPostQuoteGallery ?? [],
            setGalleryItems: setLinkPostQuoteGalleryItems,
          };
        case PostType.IGDB:
          return articleGallery;
        case PostType.POLL:
          return {
            galleryItems: pollPostGallery,
            setGalleryItems: setPollPostGalleryItems,
          };
        default:
          return {
            galleryItems: [],
            setGalleryItems: () => null,
          };
      }
    }, [
      fullPostGallery,
      fullPostTeaserStyle,
      isArticle,
      linkPostArticleGallery,
      linkPostArticleStyles?.teaserImageStyle,
      linkPostQuoteGallery,
      pollPostGallery,
      postType,
      setFullPostGalleryItems,
      setFullPostTeaserStyle,
      setLinkPostArticleGalleryItems,
      setLinkPostArticleTeaserStyle,
      setLinkPostQuoteGalleryItems,
      setPollPostGalleryItems,
      setShortPostGalleryItems,
      shortPostGallery,
    ]);

    const buildCardFilter = useCallback(
      (postInStory?: Story, backgroundColor?: string): FilterValue[] => [
        {
          groupId: 'card',
          groupItems: getCardGroupItems({ editMode, postInStory, cardOptions }),
        },
        {
          groupId: 'localeId',
          groupItems: getLocaleGroupItems({ editMode, postInStory, localeId }),
        },
        {
          groupId: 'backgroundColor',
          groupItems: getBackgroundColorGroupItems({ editMode, postInStory, backgroundColor }),
        },
      ],
      [cardOptions, editMode, localeId]
    );

    const [cardFilter, setCardFilter] = useState<FilterValue[]>(() =>
      buildCardFilter(postInStory, cardContent?.styles.backgroundColor)
    );

    useEffect(() => {
      if (editMode) {
        return;
      }

      setCardFilter(buildCardFilter(postInStory));
    }, [buildCardFilter, editMode, postInStory]);

    const postLocale = LOCALES.find(
      ({ id }) => id === getGroupValue({ filter: cardFilter, groupId: 'localeId' })
    )?.code;

    const showSettingsMedia = useMemo(
      () => Boolean(teaserStyle && galleryItems.length),
      [galleryItems, teaserStyle]
    );

    const renderSettingsToggle = useMemo(
      () =>
        Boolean(
          !isTemplateEditor &&
            (isAdminLayout() || (!isCategoryCard && (showSettingsMedia || hasContentTags)))
        ),
      [hasContentTags, isCategoryCard, isTemplateEditor, showSettingsMedia]
    );

    const storyCardOptions = useMemo(() => {
      const cardGroup = { filter: cardFilter, groupId: 'card' };

      return {
        options: {
          allowComment: Boolean(
            getGroupItemValue({ ...cardGroup, itemId: CardProps.allowComments })
          ),
          allowCardReaction: Boolean(
            getGroupItemValue({ ...cardGroup, itemId: CardProps.allowCardReactions })
          ),
          allowSharing: Boolean(
            getGroupItemValue({ ...cardGroup, itemId: CardProps.allowSharing })
          ),
          allowDisplayAuthor: Boolean(
            getGroupItemValue({ ...cardGroup, itemId: CardProps.showAllAuthors })
          ),
          shouldUpdatePostedTimeByComments: Boolean(
            getGroupItemValue({ ...cardGroup, itemId: CardProps.shouldUpdatePostedTimeByComments })
          ),
        },
      };
    }, [cardFilter, getGroupItemValue]);

    const templateSaveHandler = useCallback(
      () =>
        onTemplateSave?.(
          getFieldsByStoryCardType({
            shortPostFields,
            fullPostFields,
            linkPostFields,
            threadPostFields,
            pollPostFields,
            eventPostFields,
            postInCardType,
          }) as StoryCardTemplateStoryCardInput
        ),
      [
        eventPostFields,
        fullPostFields,
        linkPostFields,
        onTemplateSave,
        pollPostFields,
        postInCardType,
        shortPostFields,
        threadPostFields,
      ]
    );

    const postCard = useCallback(
      (status: StoryCardStatus) => {
        return new Promise<void>(async (resolve, reject) => {
          fetchCardAttemptsCount.current = 0;
          toggleStoryCardDisabled({ storyCardId: cardId, disabled: true });
          onClose();

          try {
            const backgroundColor = getGroupValue({
              filter: cardFilter,
              groupId: 'backgroundColor',
            });

            const localeId = getGroupValue({
              filter: cardFilter,
              groupId: 'localeId',
            });

            const fields = {
              ...getFieldsByStoryCardType({
                shortPostFields,
                fullPostFields,
                linkPostFields,
                threadPostFields,
                pollPostFields,
                eventPostFields,
                postInCardType,
                backgroundColor,
              }),
              ...storyCardOptions,
              status,
              ...(postType === PostType.EVENT && { eventId: eventPostFields.event?.id }),
              postingTime: postingTime?.toISOString() ?? null,
              mentionedUsersId: mentionedUsers.map(({ id }) => id),
              organisationTags: {
                externalTagsId: externalTags.map(({ id }) => id),
                externalStatusTagsId: externalStatusTags.map(({ id }) => id),
                internalTagsId: internalTags.map(({ id }) => id),
              },
              ...(!cardCategory && { localeId }),
              ...(!editMode && cardCategory && { categoryId }),
            } as StoryCardFieldsPostInStoryInput;

            const postInStoryArgs = { storyId: categoryStoryId ?? postInStoryId, fields };

            const cardUpdateArgs = {
              storyId: categoryStoryId ?? cardStoryId ?? postInStoryId,
              storyCardId: cardId,
              fields: {
                ...fields,
                ...(postInStoryId !== cardStoryId && { moveToStoryId: postInStoryId }),
              },
            };

            const { payload, error } = await (editMode
              ? storyCardUpdate(cardUpdateArgs)
              : storyCardPostInStory(postInStoryArgs)
            ).unwrap();

            const payloadAction = editMode ? editCard : addCard;

            if (payload) {
              if (isCardMediaCompleted(payload)) {
                toggleStoryCardDisabled({ storyCardId: cardId, disabled: false });
                refreshFeeds();
                refreshStoryCardsUserStats();
                dispatch(payloadAction(payload));
                resolve();
                return;
              }

              const { storyId: postedStoryId, id: postedCardId } = payload;

              const postedCardInterval = setInterval(async () => {
                fetchCardAttemptsCount.current = fetchCardAttemptsCount.current + 1;

                const postedCard = await dispatch(
                  getCard({ storyId: postedStoryId, cardId: postedCardId })
                ).unwrap();

                if (!isCardMediaCompleted(postedCard)) {
                  if (fetchCardAttemptsCount.current === FETCH_CARD_ATTEMPTS_LIMIT) {
                    toast.dismiss('postCard');
                    toast(ctxT('long-processing'), { delay: 1000 });
                  }
                  return;
                }

                if (fetchCardAttemptsCount.current < FETCH_CARD_ATTEMPTS_LIMIT) {
                  toggleStoryCardDisabled({ storyCardId: cardId, disabled: false });
                  refreshFeeds();
                  refreshStoryCardsUserStats();
                  dispatch(payloadAction(postedCard));
                  resolve();
                  clearInterval(postedCardInterval);
                  return;
                }

                const detailsUrl = `${getWebAppUrl(
                  getChannelDomain()
                )}/details/${postedStoryId}/${postedCardId}`;

                toast.dismiss('postCard');

                toast(
                  <IconLabel
                    label={ctxT('card-processed')}
                    color={getCssVar('--base-link-text-color')}
                    hoverColor={getCssVar('--base-link-text-hover-color')}
                    onClick={() => window.open(detailsUrl, '_blank', 'noopener, noreferrer')}
                  />,
                  { autoClose: false }
                );

                clearInterval(postedCardInterval);
              }, FETCH_CARD_MS_TIMEOUT);

              return;
            }

            if (error) {
              toast.error(
                error.__typename === 'StoryCardUrlUniquenessConflictError'
                  ? ctxT('unique-conflict-error')
                  : ctxT('error-message')
              );

              reject();
            }
          } catch (_) {
            reject();
          }
        });
      },
      [
        cardCategory,
        cardFilter,
        cardId,
        cardStoryId,
        categoryId,
        categoryStoryId,
        postType,
        dispatch,
        editMode,
        eventPostFields,
        externalTags,
        fullPostFields,
        getGroupValue,
        internalTags,
        externalStatusTags,
        linkPostFields,
        mentionedUsers,
        onClose,
        pollPostFields,
        postInCardType,
        postInStoryId,
        postingTime,
        refreshFeeds,
        refreshStoryCardsUserStats,
        shortPostFields,
        storyCardOptions,
        storyCardPostInStory,
        storyCardUpdate,
        ctxT,
        threadPostFields,
      ]
    );

    const onTabChange = useCallback(
      (index: number) => {
        if (editMode) {
          return;
        }

        switch (index) {
          case TabType.FULL_TAB:
            setPostType(PostType.FULL);
            break;
          case TabType.LINK_TAB:
            setPostType(PostType.LINK);
            break;
          case TabType.THREAD_TAB:
            setPostType(PostType.THREAD);
            break;
          case TabType.EVENT_TAB:
            setPostType(PostType.EVENT);
            break;
          case TabType.POLL_TAB:
            setPostType(PostType.POLL);
            break;
          case TabType.IGDB_TAB:
            setPostType(PostType.IGDB);
            break;
          default:
            setPostType(PostType.SHORT);
            break;
        }
      },
      [editMode]
    );

    const tabContent = useCallback((content: JSX.Element) => {
      return (
        <div
          className={classNames(classes['create-card__content'], {
            [classes['create-card__content--full-screen']]: isMobileOnly,
          })}
        >
          {content}
        </div>
      );
    }, []);

    const showAllPostTabs = useMemo(
      () => !editMode && !isCategoryCard && !isTemplatePost,
      [editMode, isCategoryCard, isTemplatePost]
    );

    const isNonShortTemplate = Boolean(isTemplatePost && !shortPostFilledFields);

    const hideLongPost = useMemo(() => !isAdminLayout() && excludeLongPost, [excludeLongPost]);

    const hideThreadPost = useMemo(
      () => !isAdminLayout() && excludeThreadPost,
      [excludeThreadPost]
    );

    const hidePollPost = useMemo(
      () => isTemplateEditor || (isAdminLayout() ? excludeAdminPollPost : excludeReaderPollPost),
      [excludeAdminPollPost, excludeReaderPollPost, isTemplateEditor]
    );

    const hideEventPost = useMemo(
      () => isTemplateEditor || (isAdminLayout() ? excludeAdminEventPost : excludeReaderEventPost),
      [excludeAdminEventPost, excludeReaderEventPost, isTemplateEditor]
    );

    const tabItems: TabItem[] = useMemo(
      () => [
        ...optArrItem((!editMode && !isNonShortTemplate) || isShortPost, {
          index: TabType.SHORT_TAB,
          name: isCategoryCard ? categoryName : ctxT('short-post'),
          content: tabContent(
            <ShortPost
              loading={loading}
              setLoading={setLoading}
              shortPostEditorRef={shortPostEditorRef}
              shortPostFields={shortPostFields}
              setShortPostFields={setShortPostFields}
              setShortPostGalleryItems={setShortPostGalleryItems}
              convertToLinkPost={convertToLinkPost}
              setSettingsOpen={setSettingsOpenWithScroll}
              canOpenSettings={!isTemplateEditor}
              setActiveShortPostSettingsTabIndex={setActiveSettingsTabIndex}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
              category={cardCategory}
              {...(files && { files })}
              {...(fileUrl && { fileUrl })}
              {...(applePodcastUrl && { applePodcastUrl })}
            />
          ),
        }),
        ...optArrItem((showAllPostTabs && !hideLongPost) || isFullPost, {
          index: TabType.FULL_TAB,
          name: ctxT('full-post'),
          content: tabContent(
            <FullPost
              loading={loading}
              setLoading={setLoading}
              fullPostFields={fullPostFields}
              setFullPostFields={setFullPostFields}
              setFullPostGalleryItems={setFullPostGalleryItems}
              fullPostTextEditorOpen={fullPostTextEditorOpen}
              setFullPostTextEditorOpen={setFullPostTextEditorOpen}
              fullPostTextEditorUsePrefill={fullPostTextEditorUsePrefill}
              setFullPostTextEditorUsePrefill={setFullPostTextEditorUsePrefill}
              setSettingsOpen={setSettingsOpenWithScroll}
              canOpenSettings={!isTemplateEditor}
              setActiveFullPostSettingsTabIndex={setActiveSettingsTabIndex}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
              locale={postLocale}
            />
          ),
        }),
        ...optArrItem(showAllPostTabs || isLinkPost, {
          index: TabType.LINK_TAB,
          name: ctxT('link-post'),
          content: tabContent(
            <LinkPost
              loading={loading}
              setLoading={setLoading}
              linkPostFields={linkPostFields}
              setLinkPostFields={setLinkPostFields}
              linkPostPrevUrl={linkPostPrevUrl}
              setLinkPostPrevUrl={setLinkPostPrevUrl}
              setLinkPostArticleGalleryItems={setLinkPostArticleGalleryItems}
              setLinkPostQuoteGalleryItems={setLinkPostQuoteGalleryItems}
              setSettingsOpen={setSettingsOpenWithScroll}
              canOpenSettings={!isTemplateEditor}
              setActiveLinkPostSettingsTabIndex={setActiveSettingsTabIndex}
              postInStoryTeaserStyle={postInStoryTeaserStyle}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
            />
          ),
        }),
        ...optArrItem((showAllPostTabs && !hideThreadPost) || postType === PostType.THREAD, {
          index: TabType.THREAD_TAB,
          name: ctxT('thread-post'),
          content: tabContent(
            <ThreadPost
              threadPostFields={threadPostFields}
              setThreadPostFields={setThreadPostFields}
              setActiveThreadPostSettingsTabIndex={setActiveSettingsTabIndex}
              setSettingsOpen={setSettingsOpenWithScroll}
              canOpenSettings={!isTemplateEditor}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
            />
          ),
        }),
        ...optArrItem((showAllPostTabs && !hideEventPost) || postType === PostType.EVENT, {
          index: TabType.EVENT_TAB,
          name: ctxT('event-post'),
          content: tabContent(
            <EventPost
              eventPostFields={eventPostFields}
              setEventPostFields={setEventPostFields}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
              setSettingsOpen={setSettingsOpenWithScroll}
              setActiveEventPostSettingsTabIndex={setActiveSettingsTabIndex}
            />
          ),
        }),
        ...optArrItem((showAllPostTabs && !hidePollPost) || postType === PostType.POLL, {
          index: TabType.POLL_TAB,
          name: ctxT('poll-post'),
          content: tabContent(
            <PollPost
              loading={loading}
              setLoading={setLoading}
              pollPostFields={pollPostFields}
              setPollPostFields={setPollPostFields}
              setPollPostGalleryItems={setPollPostGalleryItems}
              setSettingsOpen={setSettingsOpenWithScroll}
              setActivePollPostSettingsTabIndex={setActiveSettingsTabIndex}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
              cardStatus={cardStatus}
            />
          ),
        }),
        ...optArrItem((showAllPostTabs && enableIgdb) || isIgdbPost, {
          index: TabType.IGDB_TAB,
          name: igdbLocaliseKey ? t(igdbLocaliseKey) : ctxT('igdb-post'),
          content: tabContent(
            <IgdbPost
              loading={loading}
              setLoading={setLoading}
              linkPostFields={linkPostFields}
              setLinkPostFields={setLinkPostFields}
              setLinkPostArticleGalleryItems={setLinkPostArticleGalleryItems}
              setSettingsOpen={setSettingsOpenWithScroll}
              canOpenSettings={!isTemplateEditor}
              setActiveLinkPostSettingsTabIndex={setActiveSettingsTabIndex}
              postInStoryTeaserStyle={postInStoryTeaserStyle}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
            />
          ),
        }),
      ],
      [
        applePodcastUrl,
        cardCategory,
        cardStatus,
        categoryName,
        convertToLinkPost,
        editMode,
        enableIgdb,
        eventPostFields,
        fileUrl,
        files,
        fullPostFields,
        fullPostTextEditorOpen,
        fullPostTextEditorUsePrefill,
        hideEventPost,
        hideLongPost,
        hidePollPost,
        hideThreadPost,
        igdbLocaliseKey,
        isCategoryCard,
        isFullPost,
        isIgdbPost,
        isLinkPost,
        isNonShortTemplate,
        isShortPost,
        isTemplateEditor,
        linkPostFields,
        linkPostPrevUrl,
        loading,
        mentionedUsers,
        pollPostFields,
        postInStoryAllowMentions,
        postInStoryTeaserStyle,
        postLocale,
        postType,
        setFullPostGalleryItems,
        setLinkPostArticleGalleryItems,
        setLinkPostQuoteGalleryItems,
        setPollPostGalleryItems,
        setSettingsOpenWithScroll,
        setShortPostGalleryItems,
        shortPostFields,
        showAllPostTabs,
        ctxT,
        t,
        tabContent,
        threadPostFields,
      ]
    );

    const body = useMemo(() => {
      return (
        <div
          className={classNames(classes['create-card'], {
            [classes['create-card--full-screen']]: isMobileOnly,
          })}
        >
          <CreateCardHeader
            postInStories={postInStories}
            postInStoryId={postInStoryId}
            setPostInStoryId={setPostInStoryId}
            postInStorageKey={postInStorageKey}
            editMode={editMode}
            isFullPost={isFullPost}
            isLinkPost={isLinkPost}
            isIgdbPost={isIgdbPost}
            isCategoryCard={isCategoryCard}
            isTemplateEditor={isTemplateEditor}
            loading={loading}
            linkPostFieldsUrl={linkPostFieldsUrl}
            postInStoryTeaserStyle={postInStoryTeaserStyle}
            setPostType={setPostType}
            setShortPostFields={setShortPostFields}
            setFullPostFields={setFullPostFields}
            setLinkPostFields={setLinkPostFields}
            setLinkPostPrevUrl={setLinkPostPrevUrl}
            setFullPostTextEditorOpen={setFullPostTextEditorOpen}
            linkPostType={linkPostType}
          />

          <Tabs
            items={tabItems}
            selectedTabIndex={TabType[`${postType}_TAB`]}
            setSelectedTabIndex={onTabChange}
            disabled={loading}
            scrollable={isMobileOnly}
            {...(isMobileOnly && {
              className: classes['create-card__tabs--full-screen'],
              tabContentClassName: classes['create-card__tab-content--full-screen'],
            })}
          />

          <CreateCardButtons
            settingsButton={settingsButton}
            settingsOpen={settingsOpen}
            setSettingsOpen={setSettingsOpen}
            scrollToSettings={scrollToSettings}
            renderSettingsToggle={renderSettingsToggle}
            postType={postType}
            postingTime={postingTime}
            setPostingTime={setPostingTime}
            cardStatus={cardStatus}
            shortPostText={shortPostText}
            shortPostGallery={shortPostGallery}
            fullPostTitle={fullPostTitle}
            threadPostTitle={threadPostTitle}
            pollPostHeadline={pollPostHeadline}
            pollPostOptions={pollPostOptions}
            eventId={eventId}
            linkPostType={linkPostType}
            linkPostArticle={linkPostArticle}
            linkPostQuote={linkPostQuote}
            isCategoryCard={isCategoryCard}
            editMode={editMode}
            loading={loading}
            postCard={postCard}
            {...(isTemplateEditor && { onTemplateSave: templateSaveHandler, isTemplateSaving })}
          />

          {renderSettingsToggle && settingsOpen && (
            <CreateCardSettings
              activeSettingsTabIndex={activeSettingsTabIndex}
              setActiveSettingsTabIndex={setActiveSettingsTabIndex}
              externalTags={externalTags}
              setExternalTags={setExternalTags}
              externalStatusTags={externalStatusTags}
              setExternalStatusTags={setExternalStatusTags}
              internalTags={internalTags}
              setInternalTags={setInternalTags}
              cardFilter={cardFilter}
              setCardFilter={setCardFilter}
              showEnableComments={postInCardType !== StoryCardType.THREAD}
              showDisplayUser={!isIntegrationCard}
              onTabChangeCallback={scrollToSettings}
              showMedia={showSettingsMedia}
              teaserStyle={teaserStyle}
              setTeaserStyle={setTeaserStyle}
            />
          )}

          {userMentionModalOpen && (
            <UserMentionModal
              isOpen={userMentionModalOpen}
              onClose={userMentionModalClose}
              mentionedUsers={mentionedUsers}
              setMentionedUsers={setMentionedUsers}
            />
          )}
        </div>
      );
    }, [
      activeSettingsTabIndex,
      cardFilter,
      cardStatus,
      editMode,
      eventId,
      externalTags,
      fullPostTitle,
      internalTags,
      externalStatusTags,
      isCategoryCard,
      isFullPost,
      isIgdbPost,
      isIntegrationCard,
      isLinkPost,
      isTemplateEditor,
      isTemplateSaving,
      linkPostArticle,
      linkPostFieldsUrl,
      linkPostQuote,
      linkPostType,
      loading,
      mentionedUsers,
      onTabChange,
      pollPostHeadline,
      pollPostOptions,
      postCard,
      postInCardType,
      postInStorageKey,
      postInStories,
      postInStoryId,
      postInStoryTeaserStyle,
      postType,
      postingTime,
      renderSettingsToggle,
      scrollToSettings,
      settingsOpen,
      setTeaserStyle,
      shortPostGallery,
      shortPostText,
      showSettingsMedia,
      tabItems,
      teaserStyle,
      templateSaveHandler,
      threadPostTitle,
      userMentionModalClose,
      userMentionModalOpen,
    ]);

    return (
      <>
        <Modal
          isOpen={isOpen}
          body={body}
          onClose={onCloseHandler}
          fullScreen={isMobileOnly}
          ariaLabel={t(`common.${editMode ? 'edit' : 'create'}-post`)}
          alignTop
          disableCloseByOverlay
        />

        <ConfirmationModal
          isOpen={showCloseConfirmation}
          title={ctxT('close-confirmation-title')}
          subTitle={ctxT('close-confirmation-subtitle')}
          acceptLabel={ctxT('close-confirmation-discard')}
          cancelLabel={ctxT('close-confirmation-back')}
          onAccept={onClose}
          onClose={() => setShowCloseConfirmation(false)}
          contentStyle={{ width: '26rem' }}
        />
      </>
    );
  }
);
